import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";

const PATH = "/sitemap";

const Sitemap = () => {
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "Site Map - Female Partner Calculator",
    description: meta?.description ?? "",
    canonical: canonicalUrl(PATH),
    noindex: meta?.noindex,
  });

  const pages = [
    {
      path: "/",
      title: "Female Partner Calculator",
      description: "Calculate the statistical probability of finding a compatible female partner based on demographic and behavioral criteria.",
      priority: "High",
      updated: "Weekly"
    },
    {
      path: "/male-partner-calculator",
      title: "Male Partner Calculator",
      description: "Calculate the share of US men matching preferences for height, income, education, and more.",
      priority: "High",
      updated: "Weekly"
    },
    {
      path: "/ideal-woman-calculator",
      title: "Ideal Woman Calculator",
      description: "See what percentage of US women match your ideal for age, height, body type, income, and education.",
      priority: "High",
      updated: "Weekly"
    },
    {
      path: "/dating-standards-calculator",
      title: "Dating Standards Calculator",
      description: "Test how realistic your dating standards are with a probability of finding a partner test.",
      priority: "High",
      updated: "Weekly"
    },
    {
      path: "/stats",
      title: "US Dating Statistics Hub",
      description: "Research articles on the numbers behind the US dating pool: marriage rates, income, education, height, and more.",
      priority: "High",
      updated: "Weekly"
    },
    {
      path: "/states",
      title: "Dating Pool Statistics by State",
      description: "Estimated women aged 18-44, education levels, and marriage statistics for all 50 states and DC.",
      priority: "High",
      updated: "Monthly"
    },
    {
      path: "/faq",
      title: "Frequently Asked Questions",
      description: "Common questions about the calculator methodology, data sources, and statistical interpretations.",
      priority: "High",
      updated: "Monthly"
    },
    {
      path: "/methodology",
      title: "Calculation Methodology",
      description: "Detailed explanation of the statistical methods and probability calculations used in the compatibility assessment.",
      priority: "Medium",
      updated: "Monthly"
    },
    {
      path: "/data-sources",
      title: "Data Sources",
      description: "Comprehensive list of government agencies and research institutions providing demographic data.",
      priority: "Medium",
      updated: "Quarterly"
    },
    {
      path: "/glossary",
      title: "Statistical Glossary",
      description: "Definitions of statistical terms and concepts used throughout the calculator and documentation.",
      priority: "Low",
      updated: "Quarterly"
    },
    {
      path: "/about",
      title: "About",
      description: "Learn about Female Partner Calculator, our principles, and the team behind the tool.",
      priority: "Low",
      updated: "Yearly"
    },
    {
      path: "/privacy",
      title: "Privacy Policy",
      description: "How Female Partner Calculator handles analytics, cookies, and any information collected through the site.",
      priority: "Low",
      updated: "Yearly"
    },
    {
      path: "/terms",
      title: "Terms of Service",
      description: "Terms of service, acceptable use, and limitations of liability for Female Partner Calculator.",
      priority: "Low",
      updated: "Yearly"
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High": return "bg-destructive text-destructive-foreground";
      case "Medium": return "bg-primary text-primary-foreground";
      case "Low": return "bg-secondary text-secondary-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <header className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Site Map
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Complete navigation guide to all pages and resources on the Female Partner Calculator
            </p>
          </header>

          <div className="grid gap-6">
            {pages.map((page) => (
              <Card key={page.path} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">
                        <Link 
                          to={page.path}
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          {page.title}
                        </Link>
                      </CardTitle>
                      <CardDescription className="text-base">
                        {page.description}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      <Badge className={getPriorityColor(page.priority)}>
                        {page.priority} Priority
                      </Badge>
                      <Badge variant="outline">
                        Updated {page.updated}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <code className="bg-muted px-2 py-1 rounded text-xs">
                      {page.path === "/" ? "/" : page.path}
                    </code>
                    <Link 
                      to={page.path}
                      className="text-primary hover:text-primary/80 font-medium"
                    >
                      Visit Page →
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 p-6 bg-muted rounded-lg">
            <h2 className="text-2xl font-semibold mb-4">Additional Resources</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h3 className="font-medium mb-2">Technical</h3>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• XML Sitemap: <code>/sitemap.xml</code></li>
                  <li>• Robots.txt: <code>/robots.txt</code></li>
                  <li>• OpenGraph Image: <code>/og-image.png</code></li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium mb-2">Data Standards</h3>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• U.S. Census Bureau Data</li>
                  <li>• CDC NHANES Statistics</li>
                  <li>• Pew Research Center Surveys</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Sitemap;